import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';
import { type ColumnSizingState, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import {
  buildColumnVisibility,
  buildGroupHeaderRows,
  buildLeafColumns,
  buildTableColumns,
  EMPTY_TABLE,
  formatRows,
  getResolvedActiveTab,
  matchesSearchValue,
  mergeColumnVisibilityState,
  resolveColumnValue,
  resolveSelectionModel,
  ROW_HEIGHT,
  setSearchValueForField,
  sortRowsWithColumns,
  VIRTUALIZATION_OVERSCAN,
  VIRTUALIZATION_THRESHOLD
} from './utils';
import type {
  ColumnVisibility,
  DataTableColumnGroupingModel,
  DataTableRowEventParams,
  DataTableRowSelectionModel,
  ErpDataTableV2Props,
  SearchValue,
  SearchValues,
  SortConfig,
  SortDirection,
  VirtualRange
} from './types';
import {
  buildCellSelectionKey,
  isAdditiveCellSelection,
  resolveNumericCellValue,
  summarizeNumericCells
} from './cell-selection';
import { ErpDataTableRowContextMenu, type PageAction, type RowContextMenuState } from './row-context-menu';
import { getRowContextFilterLabel, getRowContextFilterValue } from './row-context-menu-utils';
import { dispatchErpDataTableViewRow } from '@/lib/erp-data-table-events';
import { TABLE_THEME } from '@/components/custom/erp/table-theme';
import { isBusy, subscribe } from '@/lib/api-loading-store';
import { DataTableRowViewDialog } from './row-view-dialog';
import { CompactTableTabs } from './compact-table-tabs';
import { useAuth } from '@/contexts/auth-context';
import { formatMoney } from '@/lib/formatUtils';
import { ErpDataTableHead } from './table-head';
import { ErpDataTableBody } from './table-body';
import { ErpPagination } from '../pagination';
import { cn } from '@/lib/utils';

const DEFAULT_PAGE_SIZE = 20;

const buildInitialVirtualRange = (
  totalRows: number,
  shouldVirtualize: boolean,
  initialVirtualRows = 100
): VirtualRange => ({
  start: 0,
  end: shouldVirtualize ? Math.min(totalRows, Math.max(1, initialVirtualRows)) : totalRows
});

const areStringArraysEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) return false;

  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }

  return true;
};

const DEFAULT_MIN_COLUMN_WIDTH = 80;
const DEFAULT_COLUMN_HEADER_HEIGHT = 65;
const DEFAULT_GROUPED_HEADER_HEIGHT = 100;
const PAGE_ACTION_TITLES: Record<PageAction, string[]> = {
  view: ['xem'],
  edit: ['sửa'],
  delete: ['xóa', 'xoá'],
  copy: ['sao chép']
};

const isVisibleActionButton = (button: HTMLButtonElement) => {
  const style = window.getComputedStyle(button);
  return button.getClientRects().length > 0 && style.display !== 'none' && style.visibility !== 'hidden';
};

const getElementDistance = (source: HTMLElement, target: HTMLElement) => {
  const sourceRect = source.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const sourceX = sourceRect.left + sourceRect.width / 2;
  const sourceY = sourceRect.top + sourceRect.height / 2;
  const targetX = targetRect.left + targetRect.width / 2;
  const targetY = targetRect.top + targetRect.height / 2;

  return Math.hypot(sourceX - targetX, sourceY - targetY);
};

const findClosestPageActionButton = (tableRoot: HTMLElement | null, action: PageAction) => {
  if (!tableRoot || typeof document === 'undefined') return null;

  const candidates = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-erp-action-title]')).filter(
    button => PAGE_ACTION_TITLES[action].includes(button.dataset.erpActionTitle || '') && isVisibleActionButton(button)
  );

  return (
    candidates.sort((left, right) => getElementDistance(tableRoot, left) - getElementDistance(tableRoot, right))[0] ??
    null
  );
};

const formatRowViewValue = (value: unknown): string => {
  if (value == null || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Có' : 'Không';
  if (Array.isArray(value)) return value.map(formatRowViewValue).filter(Boolean).join(', ');
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const displayValue = record.name ?? record.ten ?? record.code ?? record.ma ?? record.label;
    if (displayValue != null) return String(displayValue);

    try {
      return JSON.stringify(value) || String(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const readRootCssNumber = (name: string, fallback: number) => {
  if (typeof window === 'undefined') return fallback;

  const rawValue = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const numericValue = Number(rawValue);

  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : fallback;
};

const useErpDataTableDensity = () => {
  const [density, setDensity] = useState(() => ({
    rowHeight: ROW_HEIGHT,
    columnHeaderHeight: DEFAULT_COLUMN_HEADER_HEIGHT
  }));

  useEffect(() => {
    const updateDensity = () => {
      const nextDensity = {
        rowHeight: readRootCssNumber('--erp-data-table-row-height', ROW_HEIGHT),
        columnHeaderHeight: readRootCssNumber('--erp-data-table-column-header-height', DEFAULT_COLUMN_HEADER_HEIGHT)
      };

      setDensity(previousDensity =>
        previousDensity.rowHeight === nextDensity.rowHeight &&
        previousDensity.columnHeaderHeight === nextDensity.columnHeaderHeight
          ? previousDensity
          : nextDensity
      );
    };

    updateDensity();
    window.addEventListener('resize', updateDensity);

    return () => window.removeEventListener('resize', updateDensity);
  }, []);

  return density;
};

export function ErpDataTableV2({
  tables,
  pageSize = DEFAULT_PAGE_SIZE,
  onRowClick,
  onRowDoubleClick,
  onCellClick,
  onCellContextMenu,
  rowContextMenu,
  onTabChange,
  selectedRowId,
  defaultTabIndex = 0,
  getRowId,
  totalItems = 0,
  currentPage = 0,
  onPageChange,
  onSearchChange,
  loading: loadingProp,
  disableVirtualization = false,
  forceVirtualization = false,
  virtualizationOverscan = VIRTUALIZATION_OVERSCAN,
  virtualizationInitialRows = 100,
  toolbar,
  hideHeader = false,
  showIndexColumn = true,
  hideEmptyState = false,
  disableCellTruncate = false,
  indexColumnWidth,
  scrollMode = 'auto',
  widthMode = 'content',
  enableCellKeyboardNavigation = false,
  className
}: ErpDataTableV2Props) {
  const { loading: globalLoading } = useAuth();
  const apiLoading = useSyncExternalStore(subscribe, isBusy, isBusy);
  const loading = loadingProp ?? (globalLoading || apiLoading);
  const [activeTab, setActiveTab] = useState(() => getResolvedActiveTab(tables, defaultTabIndex, defaultTabIndex));
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(() => buildColumnVisibility(tables));
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [searchValues, setSearchValues] = useState<SearchValues>({});
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [internalSelectionModels, setInternalSelectionModels] = useState<Record<number, string[]>>({});
  const [virtualRange, setVirtualRange] = useState<VirtualRange>({ start: 0, end: 0 });
  const [containerWidth, setContainerWidth] = useState(0);
  const [selectedNumericCells, setSelectedNumericCells] = useState<Record<string, number>>({});
  const [rowContextMenuState, setRowContextMenuState] = useState<RowContextMenuState | null>(null);
  const [fallbackViewRow, setFallbackViewRow] = useState<DataTableRowEventParams | null>(null);
  const didEmitSearchRef = useRef(false);
  const onSearchChangeRef = useRef(onSearchChange);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const tableRootRef = useRef<HTMLDivElement>(null);
  const fallbackRowIdsRef = useRef(new WeakMap<object, string>());
  const fallbackRowIdCounterRef = useRef(0);
  const scrollFrameRef = useRef<number | undefined>(undefined);
  const deferredSearchValues = useDeferredValue(searchValues);
  const responsiveDensity = useErpDataTableDensity();

  useEffect(() => {
    setActiveTab(previousTab => getResolvedActiveTab(tables, previousTab, defaultTabIndex));
  }, [defaultTabIndex, tables]);

  useEffect(() => {
    const nextVisibility = buildColumnVisibility(tables);
    setColumnVisibility(previousVisibility => mergeColumnVisibilityState(previousVisibility, nextVisibility));
  }, [tables]);

  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  useEffect(() => {
    const handleSearchChange = onSearchChangeRef.current;
    if (!handleSearchChange) return;

    if (!didEmitSearchRef.current) {
      didEmitSearchRef.current = true;
      return;
    }

    handleSearchChange(searchValues);
  }, [searchValues]);

  const handleTabChange = useCallback(
    (nextTab: number, event?: React.SyntheticEvent) => {
      if (nextTab === activeTab) {
        return;
      }

      setActiveTab(nextTab);
      setSelectedNumericCells({});
      onTabChange?.(event ?? ({} as React.SyntheticEvent), nextTab);
    },
    [activeTab, onTabChange]
  );

  const handleCellSelection = useCallback((event: React.MouseEvent, params: any) => {
    const isAdditiveSelection = isAdditiveCellSelection(event);

    if (!isAdditiveSelection) {
      setSelectedNumericCells({});
      return false;
    }

    const numericValue = resolveNumericCellValue(params.value, params.formattedValue);
    if (numericValue === null) return false;

    const key = buildCellSelectionKey(String(params.id), params.field);
    setSelectedNumericCells(previousSelection => {
      if (key in previousSelection) {
        const nextSelection = { ...previousSelection };
        delete nextSelection[key];
        return nextSelection;
      }

      return { ...previousSelection, [key]: numericValue };
    });

    return true;
  }, []);

  const selectedCellKeys = useMemo(() => new Set(Object.keys(selectedNumericCells)), [selectedNumericCells]);
  const numericCellSummary = useMemo(
    () => summarizeNumericCells(Object.values(selectedNumericCells)),
    [selectedNumericCells]
  );

  const currentTable = useMemo(() => tables[activeTab] ?? EMPTY_TABLE, [activeTab, tables]);
  const currentColumns = currentTable.columns;
  const formattedRows = useMemo(() => formatRows(currentTable.rows), [currentTable.rows]);

  const resolveRowId = useCallback(
    (row: any) => {
      const providedId = getRowId?.(row);

      if (providedId !== undefined && providedId !== null && providedId !== '') {
        return String(providedId);
      }

      if (row?.uuid !== undefined && row?.uuid !== null && row?.uuid !== '') {
        return String(row.uuid);
      }

      if (row?.id !== undefined && row?.id !== null && row?.id !== '') {
        return String(row.id);
      }

      if (row?.ID !== undefined && row?.ID !== null && row?.ID !== '') {
        return String(row.ID);
      }

      if (row?.code !== undefined && row?.code !== null && row?.code !== '') {
        return String(row.code);
      }

      if (row && typeof row === 'object') {
        const cachedId = fallbackRowIdsRef.current.get(row);

        if (cachedId) {
          return cachedId;
        }

        const nextId = `__row-${fallbackRowIdCounterRef.current}`;
        fallbackRowIdCounterRef.current += 1;
        fallbackRowIdsRef.current.set(row, nextId);
        return nextId;
      }

      return String(row);
    },
    [getRowId]
  );

  const currentColumnsByField = useMemo(
    () => new Map(currentColumns.map(column => [column.field, column] as const)),
    [currentColumns]
  );
  const fallbackViewFields = useMemo(() => {
    if (!fallbackViewRow) return [];

    return currentColumns
      .filter(column => columnVisibility[column.field] !== false)
      .map(column => {
        const { value } = resolveColumnValue(fallbackViewRow.row, column, fallbackViewRow.id);
        return {
          field: column.field,
          label: column.headerName || column.field,
          value: formatRowViewValue(value)
        };
      });
  }, [columnVisibility, currentColumns, fallbackViewRow]);
  const activeSearchEntries = useMemo(
    () => Object.entries(deferredSearchValues) as Array<[string, SearchValue]>,
    [deferredSearchValues]
  );

  const processedRows = useMemo(() => {
    const shouldFilterLocally = !onSearchChange && activeSearchEntries.length > 0;

    const filteredRows = shouldFilterLocally
      ? formattedRows.filter(row => {
          const rowId = resolveRowId(row);

          return activeSearchEntries.every(([field, value]) => {
            const resolvedValue = resolveColumnValue(row, currentColumnsByField.get(field), rowId).value;
            return matchesSearchValue(resolvedValue, value);
          });
        })
      : formattedRows;

    return sortRowsWithColumns(filteredRows, sortConfig, currentColumns, resolveRowId);
  }, [
    activeSearchEntries,
    currentColumns,
    currentColumnsByField,
    formattedRows,
    onSearchChange,
    resolveRowId,
    sortConfig
  ]);

  const checkboxSelection = Boolean(currentTable.checkboxSelection);
  const leafColumns = useMemo(
    () => buildLeafColumns(currentColumns, columnVisibility, checkboxSelection, showIndexColumn, indexColumnWidth),
    [checkboxSelection, columnVisibility, currentColumns, indexColumnWidth, showIndexColumn]
  );
  const tableColumns = useMemo(() => buildTableColumns(leafColumns), [leafColumns]);
  const stickyLeafColumns = useMemo(() => leafColumns.filter(column => Boolean(column.sticky)), [leafColumns]);
  const dataLeafColumns = useMemo(() => leafColumns.filter(column => !column.sticky), [leafColumns]);
  const groupedHeaderRows = useMemo(
    () =>
      buildGroupHeaderRows(
        dataLeafColumns,
        currentTable.columnGroupingModel as DataTableColumnGroupingModel | undefined
      ),
    [currentTable.columnGroupingModel, dataLeafColumns]
  );
  const rowSelectionModel = useMemo(
    () => resolveSelectionModel(currentTable.selectionModel ?? internalSelectionModels[activeTab]),
    [activeTab, currentTable.selectionModel, internalSelectionModels]
  );
  const rowSelectionSet = useMemo(() => new Set(rowSelectionModel), [rowSelectionModel]);
  const hasManualColumnSizing = useMemo(() => Object.keys(columnSizing).length > 0, [columnSizing]);
  const sizingVersion = useMemo(
    () =>
      Object.entries(columnSizing)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, value]) => `${key}:${value}`)
        .join('|'),
    [columnSizing]
  );
  const effectiveWidthMode = widthMode === 'stretch' && !hasManualColumnSizing ? 'stretch' : 'content';

  const table = useReactTable({
    data: processedRows,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: originalRow => resolveRowId(originalRow),
    state: {
      columnSizing
    },
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: 'onChange',
    enableColumnResizing: true
  });

  const rowModelRows = table.getRowModel().rows;
  const totalRowCount = rowModelRows.length;
  const resolvedTableWidthMode = effectiveWidthMode === 'stretch' ? 'stretch' : 'content';
  const allCurrentRowIds = useMemo(() => processedRows.map(row => resolveRowId(row)), [processedRows, resolveRowId]);
  const allRowsSelected = allCurrentRowIds.length > 0 && allCurrentRowIds.every(id => rowSelectionSet.has(id));
  const someRowsSelected = allCurrentRowIds.some(id => rowSelectionSet.has(id));
  const tableWidth = useMemo(() => {
    void sizingVersion;
    return leafColumns.reduce((total, column) => total + (table.getColumn(column.id)?.getSize() ?? column.width), 0);
  }, [leafColumns, sizingVersion, table]);
  const renderedColumnWidths = useMemo(() => {
    void sizingVersion;
    return Object.fromEntries(
      leafColumns.map(column => [column.id, table.getColumn(column.id)?.getSize() ?? column.width])
    ) as Record<string, number>;
  }, [leafColumns, sizingVersion, table]);
  const renderedTableWidth = useMemo(
    () => leafColumns.reduce((total, column) => total + (renderedColumnWidths[column.id] ?? column.width), 0),
    [leafColumns, renderedColumnWidths]
  );

  const hasGroupedHeaders = groupedHeaderRows.maxDepth > 0;
  const disableColumnFilters = Boolean(currentTable.disableColumnFilters);
  const responsiveColumnHeaderHeight = disableColumnFilters ? 30 : responsiveDensity.columnHeaderHeight;
  const resolvedColumnHeaderHeight = currentTable.columnHeaderHeight ?? responsiveColumnHeaderHeight;
  const resolvedHeaderHeight =
    currentTable.headerHeight ??
    (currentTable.columnGroupingModel
      ? Math.max(
          DEFAULT_GROUPED_HEADER_HEIGHT * (responsiveDensity.columnHeaderHeight / DEFAULT_COLUMN_HEADER_HEIGHT),
          resolvedColumnHeaderHeight
        )
      : resolvedColumnHeaderHeight);
  const rowHeightGetter = typeof currentTable.rowHeight === 'function' ? currentTable.rowHeight : undefined;
  const resolvedRowHeight =
    typeof currentTable.rowHeight === 'number' ? currentTable.rowHeight : responsiveDensity.rowHeight;
  const resolvedVirtualizationOverscan = Math.max(0, Math.floor(virtualizationOverscan));
  const groupRowHeight = hasGroupedHeaders
    ? Math.max((resolvedHeaderHeight - resolvedColumnHeaderHeight) / groupedHeaderRows.maxDepth, 1)
    : 0;
  const resizingColumnId = table.getState().columnSizingInfo.isResizingColumn;
  const shouldVirtualize =
    !disableVirtualization && !rowHeightGetter && (forceVirtualization || totalRowCount > VIRTUALIZATION_THRESHOLD);

  const updateVirtualRange = useCallback(() => {
    const container = scrollContainerRef.current;

    if (!container || !shouldVirtualize) {
      const nextRange = buildInitialVirtualRange(totalRowCount, shouldVirtualize, virtualizationInitialRows);
      setVirtualRange(previousRange =>
        previousRange.start === nextRange.start && previousRange.end === nextRange.end ? previousRange : nextRange
      );
      return;
    }

    const visibleCount = Math.max(
      Math.ceil(container.clientHeight / resolvedRowHeight) + resolvedVirtualizationOverscan * 2,
      resolvedVirtualizationOverscan * 2,
      1
    );
    const start = Math.max(Math.floor(container.scrollTop / resolvedRowHeight) - resolvedVirtualizationOverscan, 0);
    const end = Math.min(start + visibleCount, totalRowCount);

    setVirtualRange(previousRange =>
      previousRange.start === start && previousRange.end === end ? previousRange : { start, end }
    );
  }, [resolvedRowHeight, resolvedVirtualizationOverscan, shouldVirtualize, totalRowCount, virtualizationInitialRows]);

  const scheduleVirtualRangeUpdate = useCallback(() => {
    if (scrollFrameRef.current !== undefined) {
      return;
    }

    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = undefined;
      updateVirtualRange();
    });
  }, [updateVirtualRange]);

  useEffect(() => {
    updateVirtualRange();

    const container = scrollContainerRef.current;
    if (!container) return;

    setContainerWidth(container.clientWidth);

    const resizeObserver = new ResizeObserver(() => {
      setContainerWidth(container.clientWidth);
      updateVirtualRange();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateVirtualRange]);

  useEffect(
    () => () => {
      if (scrollFrameRef.current !== undefined) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const container = scrollContainerRef.current;

    if (container) {
      container.scrollTop = 0;
      container.scrollLeft = 0;
    }

    setVirtualRange(buildInitialVirtualRange(totalRowCount, shouldVirtualize, virtualizationInitialRows));
  }, [activeTab, currentPage, shouldVirtualize, totalRowCount, virtualizationInitialRows]);

  useEffect(() => {
    if (!sortConfig?.field) return;

    if (!currentColumnsByField.has(sortConfig.field)) {
      setSortConfig(null);
    }
  }, [currentColumnsByField, sortConfig]);

  const visibleRows = shouldVirtualize ? rowModelRows.slice(virtualRange.start, virtualRange.end) : rowModelRows;
  const topSpacerHeight = shouldVirtualize ? virtualRange.start * resolvedRowHeight : 0;
  const bottomSpacerHeight = shouldVirtualize ? (totalRowCount - virtualRange.end) * resolvedRowHeight : 0;

  const handleSearch = useCallback((field: string, value: string | string[]) => {
    setSearchValues(previousValues => setSearchValueForField(previousValues, field, value));
  }, []);

  const handleSort = useCallback((field: string, direction: SortDirection) => {
    setSortConfig(direction ? { field, direction } : null);
  }, []);

  const handleHideColumn = useCallback((field: string) => {
    setColumnVisibility(previousVisibility => {
      if (previousVisibility[field] === false) {
        return previousVisibility;
      }

      return {
        ...previousVisibility,
        [field]: false
      };
    });
  }, []);

  const closeRowContextMenu = useCallback(() => {
    setRowContextMenuState(null);
  }, []);

  const handleRowContextMenu = useCallback(
    (event: MouseEvent | React.MouseEvent, params: any) => {
      onCellContextMenu?.(event, params);
      if (rowContextMenu === false) return;

      const rowParams = { id: params.id, row: params.row };
      onRowClick?.(rowParams);

      setRowContextMenuState({
        position: { x: event.clientX, y: event.clientY },
        cell: params,
        pageActions: {
          view: Boolean(onRowDoubleClick || findClosestPageActionButton(tableRootRef.current, 'view')),
          edit: Boolean(findClosestPageActionButton(tableRootRef.current, 'edit')),
          delete: Boolean(findClosestPageActionButton(tableRootRef.current, 'delete')),
          copy: Boolean(findClosestPageActionButton(tableRootRef.current, 'copy'))
        }
      });
    },
    [onCellContextMenu, onRowClick, onRowDoubleClick, rowContextMenu]
  );

  const handlePageAction = useCallback(
    (action: PageAction, params: { id: string; row: any }) => {
      onRowClick?.(params);

      window.setTimeout(() => {
        const actionButton = findClosestPageActionButton(tableRootRef.current, action);
        if (actionButton) {
          actionButton.click();
          return;
        }

        if (action === 'view') {
          if (onRowDoubleClick) {
            onRowDoubleClick(params);
            return;
          }

          if (!dispatchErpDataTableViewRow(params)) {
            setFallbackViewRow(params);
          }
        }
      }, 0);
    },
    [onRowClick, onRowDoubleClick]
  );

  const handleRowContextFilter = useCallback(() => {
    const cell = rowContextMenuState?.cell;
    if (!cell) return;

    handleSearch(cell.field, getRowContextFilterValue(cell));
  }, [handleSearch, rowContextMenuState]);

  const handleSelectionChange = useCallback(
    (nextSelectionModel: string[]) => {
      const normalizedSelectionModel = Array.from(new Set(nextSelectionModel.map(value => String(value))));

      if (currentTable.selectionModel === undefined) {
        setInternalSelectionModels(previousSelectionModels => {
          const previousSelectionModel = previousSelectionModels[activeTab] ?? [];

          if (areStringArraysEqual(previousSelectionModel, normalizedSelectionModel)) {
            return previousSelectionModels;
          }

          return {
            ...previousSelectionModels,
            [activeTab]: normalizedSelectionModel
          };
        });
      }

      currentTable.onSelectionModelChange?.(normalizedSelectionModel as DataTableRowSelectionModel);
    },
    [activeTab, currentTable]
  );

  const handleToggleAllRows = useCallback(
    (checked: boolean) => {
      handleSelectionChange(checked ? allCurrentRowIds : []);
    },
    [allCurrentRowIds, handleSelectionChange]
  );

  return (
    <div
      ref={tableRootRef}
      data-excel-operation-overlay-root
      className={cn(
        'flex h-full min-w-0 max-w-full flex-col overflow-hidden',
        TABLE_THEME.root,
        resizingColumnId && 'cursor-col-resize select-none',
        className
      )}
    >
      {toolbar}

      {!hideHeader && (
        <div
          className={cn('flex w-full items-center justify-between border-b bg-muted', TABLE_THEME.toolbar, '!bg-muted')}
        >
          <CompactTableTabs tables={tables} activeTab={activeTab} onChange={handleTabChange} />

          {onPageChange && (
            <ErpPagination
              paginationModel={{ page: currentPage, pageSize }}
              totalItems={totalItems}
              onPageChange={onPageChange}
              className='ml-auto'
            />
          )}
        </div>
      )}

      <div
        ref={scrollContainerRef}
        className={cn(
          'relative min-h-0 min-w-0 max-w-full flex-1 overflow-auto [&::-webkit-scrollbar-thumb]:rounded-md [&::-webkit-scrollbar-track]:rounded-md [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar]:w-2',
          TABLE_THEME.scrollbar,
          scrollMode === 'vertical' && 'overflow-y-auto overflow-x-hidden'
        )}
        onScroll={scheduleVirtualRangeUpdate}
      >
        <div
          className={cn(
            'z-0 h-full',
            TABLE_THEME.surfaceBorder,
            TABLE_THEME.surface,
            resolvedTableWidthMode === 'stretch' ? 'w-full min-w-full' : 'w-max min-w-max'
          )}
        >
          <table
            className={cn(
              'border-separate border-spacing-0 text-[length:var(--erp-data-table-text-size)] [&_td]:box-border [&_th]:box-border',
              TABLE_THEME.tableText
            )}
            style={{
              minWidth:
                resolvedTableWidthMode === 'stretch'
                  ? `${renderedTableWidth}px`
                  : renderedTableWidth
                    ? `${renderedTableWidth}px`
                    : '100%',
              width:
                resolvedTableWidthMode === 'stretch'
                  ? '100%'
                  : renderedTableWidth
                    ? `${renderedTableWidth}px`
                    : 'max-content',
              tableLayout: 'fixed'
            }}
          >
            <colgroup>
              {leafColumns.map(column => (
                <col key={column.id} style={{ width: `${renderedColumnWidths[column.id] ?? column.width}px` }} />
              ))}
            </colgroup>
            <ErpDataTableHead
              table={table}
              leafColumns={leafColumns}
              stickyLeafColumns={stickyLeafColumns}
              dataLeafColumns={dataLeafColumns}
              renderedColumnWidths={renderedColumnWidths}
              groupedHeaderRows={groupedHeaderRows}
              searchValues={searchValues}
              sortConfig={sortConfig}
              resolvedHeaderHeight={resolvedHeaderHeight}
              resolvedColumnHeaderHeight={resolvedColumnHeaderHeight}
              groupRowHeight={groupRowHeight}
              checkboxSelection={checkboxSelection}
              showIndexColumn={showIndexColumn}
              disableColumnFilters={disableColumnFilters}
              allRowsSelected={allRowsSelected}
              someRowsSelected={someRowsSelected}
              onSearch={handleSearch}
              onSort={handleSort}
              onHideColumn={handleHideColumn}
              onToggleAllRows={handleToggleAllRows}
            />

            <ErpDataTableBody
              leafColumns={leafColumns}
              renderedColumnWidths={renderedColumnWidths}
              leafColumnCount={leafColumns.length}
              visibleRows={visibleRows}
              totalRowCount={totalRowCount}
              topSpacerHeight={topSpacerHeight}
              bottomSpacerHeight={bottomSpacerHeight}
              virtualStart={virtualRange.start}
              shouldVirtualize={shouldVirtualize}
              loading={loading}
              hideEmptyState={hideEmptyState}
              disableCellTruncate={disableCellTruncate}
              rowHeight={resolvedRowHeight}
              getRowHeight={rowHeightGetter}
              selectedRowId={selectedRowId}
              onCellClick={onCellClick}
              onCellSelection={handleCellSelection}
              selectedCellKeys={selectedCellKeys}
              onCellContextMenu={handleRowContextMenu}
              rowSelectionModel={rowSelectionModel}
              checkboxSelection={checkboxSelection}
              showIndexColumn={showIndexColumn}
              enableCellKeyboardNavigation={enableCellKeyboardNavigation}
              onSelectionChange={handleSelectionChange}
              onRowClick={onRowClick}
              onRowDoubleClick={onRowDoubleClick}
            />
          </table>
        </div>
      </div>
      {numericCellSummary && (
        <div
          className='fixed inset-x-0 bottom-0 z-50 flex h-7 items-center justify-end gap-5 border-t border-gray-200 bg-white px-3 text-xs text-slate-700 shadow-[0_-1px_3px_rgba(0,0,0,0.08)]'
          role='status'
          aria-live='polite'
        >
          <span>
            Đếm: <strong>{numericCellSummary.count}</strong>
          </span>
          <span>
            Trung bình: <strong>{formatMoney(Number(numericCellSummary.average.toFixed(2)))}</strong>
          </span>
          <span>
            Tổng cộng: <strong>{formatMoney(numericCellSummary.sum)}</strong>
          </span>
          <button
            type='button'
            className='ml-1 text-base leading-none text-slate-500 hover:text-slate-900'
            aria-label='Xóa lựa chọn ô'
            title='Xóa lựa chọn ô'
            onClick={() => setSelectedNumericCells({})}
          >
            ×
          </button>
        </div>
      )}
      <ErpDataTableRowContextMenu
        state={rowContextMenuState}
        config={rowContextMenu === false ? undefined : rowContextMenu}
        filterLabel={getRowContextFilterLabel(
          rowContextMenuState ? getRowContextFilterValue(rowContextMenuState.cell) : undefined
        )}
        onClose={closeRowContextMenu}
        onFilter={handleRowContextFilter}
        onPageAction={handlePageAction}
      />
      {fallbackViewRow && (
        <DataTableRowViewDialog fields={fallbackViewFields} onClose={() => setFallbackViewRow(null)} />
      )}
    </div>
  );
}

export {
  buildDataTableCols,
  composeDataTablePresets,
  createCheckboxPreset,
  getDataTableColMeta,
  type CheckboxPresetOptions,
  type DataTableCellRenderer,
  type DataTableCol,
  type DataTableColConfig,
  type DataTableColMeta,
  type DataTableColPreset,
  type DataTableColPresetInput,
  type DataTableFilter,
  type DataTableValueFormatter,
  type DataTableValueGetter,
  type FilterOptions
} from './column-builder';
export { buildColMeta, useTableSearch } from './hook/use-table-search';
export type {
  DataTableAlign,
  DataTableColumn,
  DataTableColumnGroupingModel,
  DataTableColumnType,
  DataTableLeafColumn,
  DataTableRenderCellParams,
  DataTableRowEventHandler,
  DataTableRowEventParams,
  DataTableRowContextMenuConfig,
  DataTableRowContextMenuHandler,
  DataTableRowModel,
  DataTableRowSelectionModel,
  GridEventListener,
  ErpDataTableV2Props,
  ErpDataTableBaseProps,
  TableData,
  SearchValues,
  SearchValue,
  SearchSearchColumnDef,
  SortConfig,
  SortDirection,
  ColumnVisibility,
  SelectionState,
  VirtualRange,
  ErpDataTableTabProps
} from './types';
